import React, {FC} from 'react';
import { connect } from 'react-redux';

import {AppType} from '../../../constants';
import {SmallAppList, SmallClockList} from '../ItemsPreview';
import App from '../../../models/app';
import {shuffleArray} from '../../../utils.v2';
import fsc from 'fsc';
import {ContentBlock, Link, SectionTitle} from '../Common';
import styled from 'styled-components';
import { searchDeveloperPreview, fetchDeveloperApps } from 'actions/registry';
import { translations } from 'i18n';

const TopContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background-color: #F6F6F6;
  margin: 0;
`;

interface MoreFromDeveloperSectionProps {
  appId: string;
  appType: AppType;
  developerProfileId: string;
  developerName: string;
  fromDeveloperSectionTitle: string;
  viewAllText: string;
  searchDeveloperPreview: any;
  fetchDeveloperApps: any;
  gallery: any;
  registry: any;
  installFlow: any;
}

const mapStateToProps = (state: any) => {
  const { gallery, registry, installFlow } = state;
  const {
    app: { entry },
  } = state.registry;

  let app;
  let isClock = false;
  if (entry) {
    app = App.selectFromState(entry, state);
    isClock = app.isClock;
  }
  const {id: appId, type: appType, developer: {id: developerProfileId, name: developerName}} = app;

  const {
    page: {
      [isClock ? 'Clock' : 'App']: {
        Details: {
          fromDeveloperSectionTitle,
        },
      }
    },
    component: {
      Category: { viewAllText }
    }
  } = translations;

  return {
    gallery,
    registry,
    installFlow,
    appId,
    appType,
    developerProfileId,
    developerName,
    fromDeveloperSectionTitle: fromDeveloperSectionTitle(),
    viewAllText: viewAllText(),
  };
};

interface SearchDeveloperPreviewArgs {
  developerName?: string;
  developerProfileId: string;
}

interface FetchDeveloperAppsArgs {
  developerProfileId: string;
  type?: string;
  startIndex?: number;
  limit?: number;
}

const mapDispatchToProps = (dispatch: any) => ({
  searchDeveloperPreview: ({ developerName, developerProfileId }: SearchDeveloperPreviewArgs) => dispatch(searchDeveloperPreview(
    developerName,
    developerProfileId,
  )),
  fetchDeveloperApps: ({developerProfileId, type, startIndex, limit }: FetchDeveloperAppsArgs) => dispatch(fetchDeveloperApps(
    {
      developerProfileId,
      type,
      startIndex,
      limit,
    }
  )),
});

const MoreFromDeveloperSection: FC<MoreFromDeveloperSectionProps> = (
  {
    appId, appType, developerProfileId, developerName,
    fromDeveloperSectionTitle, viewAllText,
    searchDeveloperPreview, fetchDeveloperApps,
    gallery, registry, installFlow
  }
) => {
  const appTypeLowerCase = appType.toLowerCase();
  // Support only for apps and clocks at the moment.
  if (appTypeLowerCase !== AppType.APP && appTypeLowerCase !== AppType.CLOCK) {
    return <span/>
  }
  const fetchId = `${developerProfileId}-${appType}-limit:4`;

  if (!registry.fetchDeveloperApps) {
    fetchDeveloperApps({developerProfileId, type:appType, limit: 4});
  }

  if (registry.fetchDeveloperApps.loading) {
    return <span/>;
  }

  if (!registry.fetchDeveloperApps[fetchId]) {
    fetchDeveloperApps({developerProfileId, type:appType, limit: 4});
  }

  if (!registry.fetchDeveloperApps[fetchId]) {
    return <span/>;
  }

  let developerAppIds = registry.fetchDeveloperApps[fetchId].entries;
  if (!developerAppIds) {
    return <span/>;
  }
  developerAppIds = developerAppIds.filter(
      (devAppId: string) => devAppId !== appId
    )
  if (developerAppIds.length === 0) {
    return <span/>;
  }
  const showcasedDeveloperApps = developerAppIds.slice(0, 3).map((appId: string) => App.selectFromState(appId, { gallery, registry, installFlow }));

  const moreFromDeveloperListProps = {
    apps: showcasedDeveloperApps,
    onClick: (appId: string) => fsc
      .view('Apps')
      .Tapped(`Developer ${appTypeLowerCase === AppType.APP ? "App" : "Clock"} Preview`)
      .withPayload({ appId: appId, developerProfileId: developerProfileId})
      .send()
  }
  const MoreFromDeveloperList = appTypeLowerCase === AppType.APP ? SmallAppList : SmallClockList;

  return (
    <div>
      <TopContainer>
        <SectionTitle
          id="more-from-developer"
          aria-label="more-from-developer"
        >{fromDeveloperSectionTitle}</SectionTitle>
        {
          registry.features.flags.showSearch && (
            <SectionTitle nearRightEdge={true}>
              <Link
                id="view-all-more-from-developer"
                aria-label="view-all-more-from-developer"
                to={`/v2/search`}
                pageDirection="FORWARD"
                onClick={() => {
                  fsc
                    .view(appType.toLowerCase() === AppType.APP ? 'Apps' : 'Clocks')
                    .Tapped(`View All developer ${appType.toLowerCase() === AppType.APP ? 'apps' : 'clocks'}`)
                    .withPayload({
                      appId: appId,
                      developerProfileId: developerProfileId
                    })
                    .send()
                  searchDeveloperPreview({developerName, developerProfileId});
                }
              }
              >
                {viewAllText}
              </Link>
            </SectionTitle>
          )
        }
      </TopContainer>
      <ContentBlock
        borderTop={false}
        borderBottom={false}
        paddingTop={appType.toLowerCase() === AppType.CLOCK}
        paddingBottom={false}
        paddingLeft={false}
        paddingRight={false}
        lineHeight={false}
      >
        <MoreFromDeveloperList {...moreFromDeveloperListProps}/>
      </ContentBlock>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MoreFromDeveloperSection);
